import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, ExternalLink } from "lucide-react";

const CATEGORY_MAP = {
  helpline:      { label: "National Helpline",             match: ["helpline", "hotline", "crisis", "emergency"] },
  detox:         { label: "Detox Facilities",              match: ["detox", "withdrawal"] },
  programs:      { label: "In & Out-Patient Programs",     match: ["inpatient", "outpatient", "rehab", "treatment", "program"] },
  "sober-living":{ label: "Sober Livings",                 match: ["sober living", "sober-living", "sober house", "halfway", "recovery house"] },
  meetings:      { label: "12-Step Meeting Schedules",     match: ["meeting", "12-step", "12 step", "twelve step", "fellowship", "n.a.", "na "] },
  other:         { label: "Other Resources",               match: [] },
};

function categorize(res) {
  const text = `${res.category || ""} ${res.title || ""} ${res.description || ""}`.toLowerCase();
  for (const [slug, def] of Object.entries(CATEGORY_MAP)) {
    if (def.match.some((k) => text.includes(k))) return slug;
  }
  return "other";
}

export default function Resources() {
  const [resources, setResources] = useState([]);
  const location = useLocation();

  useEffect(() => {
    api.get("/resources").then((r) => setResources(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Wait a tick for content to render
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  }, [location.hash, resources.length]);

  const bySlug = resources.reduce((acc, r) => {
    const slug = categorize(r);
    (acc[slug] = acc[slug] || []).push(r);
    return acc;
  }, {});

  const order = ["helpline", "detox", "programs", "sober-living", "meetings", "other"];

  return (
    <div data-testid="resources-page" className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="overline mb-4">For the journey out</div>
      <h1 className="font-serif-display text-5xl md:text-6xl leading-none mb-4">Resources</h1>
      <p className="text-[var(--ink-soft)] text-lg max-w-2xl leading-relaxed mb-10">
        Hotlines, meeting finders, treatment options, reading we trust, and tools for families.
        Everything here is something we&apos;ve used or someone we love has used.
      </p>
      <div id="helpline" className="mb-16 p-6 border-l-4 border-[var(--terracotta)] bg-[var(--bg-alt)] text-sm scroll-mt-40">
        <div className="font-mono-acc text-xs tracking-wider text-[var(--forest)] mb-2">IF YOU NEED HELP RIGHT NOW</div>
        <div className="text-[var(--ink)]">
          <strong>SAMHSA National Helpline:</strong> 1-800-662-HELP (4357) — free, confidential, 24/7, in English &amp; Spanish.
          <br />
          <strong>988 Suicide &amp; Crisis Lifeline:</strong> Call or text <strong>988</strong>.
        </div>
      </div>

      {resources.length === 0 ? (
        <div className="text-center py-16 text-[var(--ink-soft)]">No resources posted yet.</div>
      ) : (
        <div className="space-y-16">
          {order.map((slug) => {
            const items = bySlug[slug];
            if (!items || !items.length) return null;
            return (
              <div key={slug} id={slug} className="scroll-mt-40">
                <div className="overline mb-6">{CATEGORY_MAP[slug].label}</div>
                <div className="grid md:grid-cols-2 gap-6">
                  {items.map((r) => (
                    <article key={r.id} data-testid={`resource-${r.id}`}
                      className="p-8 border border-[var(--line)] rounded-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 bg-[var(--bg)]">
                      <BookOpen className="w-5 h-5 text-[var(--forest)] mb-4" strokeWidth={1.5} />
                      <h3 className="font-serif-display text-2xl mb-3">{r.title}</h3>
                      <p className="text-[var(--ink-soft)] text-sm leading-relaxed mb-4">{r.description}</p>
                      {r.body && <p className="text-[var(--ink-soft)] text-sm leading-relaxed mb-4 whitespace-pre-line">{r.body}</p>}
                      {r.url && (
                        <a href={r.url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm text-[var(--forest)] hover:underline">
                          Open resource <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
